/*
 * THE WELCOME LINE.
 *
 * A rotation of twenty short greetings, narrowed by the local hour and then picked from what
 * remains. Kept as a pure function of (name, hour, seed) so the whole thing is testable without a
 * DOM and without waiting for 3am to see the 3am line.
 *
 * THREE RULES, each of which is a defect in the obvious version:
 *
 *   • TRANSLATABLE. Every line is an i18n key of a few plain words — no puns, no idiom that only
 *     lands in English, no sentence assembled from fragments. A greeting built by concatenation
 *     ("Good " + partOfDay + ", " + name) cannot be translated into a language that orders those
 *     differently, and a locale file is where that has to be fixable.
 *   • STABLE WHILE IT IS ON SCREEN. The seed is chosen once by the caller, not read from a clock
 *     inside the render. Deriving it per render means the greeting reshuffles on every keystroke,
 *     which is the sort of motion nobody asks for and everybody notices.
 *   • ROTATING BETWEEN VISITS. A greeting that is always the same is furniture and stops being
 *     read. Different seed, different line, still appropriate to the hour.
 *
 * ON "LOCATION": the local hour IS the location-derived part, and it comes from the browser's own
 * clock — which already reflects where the machine thinks it is. Geolocation is deliberately not
 * used: it needs a permission prompt, and asking for someone's coordinates to decide between
 * "morning" and "evening" is a poor trade for them.
 */
/**
 * The rotation. Twenty lines: eight anchored to a part of the day, twelve that fit any hour.
 *
 * The mix is deliberate. All-time-anchored would give four possible greetings and read as a clock;
 * all-neutral would never acknowledge that it is 2am. Weighted toward neutral so the time-specific
 * ones stay a pleasant surprise rather than the whole personality.
 */
export const GREETINGS = [
    { key: 'morning1', when: 'morning' },
    { key: 'morning2', when: 'morning' },
    { key: 'afternoon1', when: 'afternoon' },
    { key: 'afternoon2', when: 'afternoon' },
    { key: 'evening1', when: 'evening' },
    { key: 'evening2', when: 'evening' },
    { key: 'night1', when: 'night' },
    { key: 'night2', when: 'night' },
    { key: 'any1', when: 'any' },
    { key: 'any2', when: 'any' },
    { key: 'any3', when: 'any' },
    { key: 'any4', when: 'any' },
    { key: 'any5', when: 'any' },
    { key: 'any6', when: 'any' },
    { key: 'any7', when: 'any' },
    { key: 'any8', when: 'any' },
    { key: 'any9', when: 'any' },
    { key: 'any10', when: 'any' },
    { key: 'any11', when: 'any' },
    { key: 'any12', when: 'any' },
];
/**
 * Which part of the day an hour belongs to.
 *
 * Night runs from 22:00 to 05:00 and is the only bucket that WRAPS midnight — the case a naive
 * range check gets wrong, because 23 and 2 are both night and no single `a <= h && h < b` covers
 * them.
 */
export function partOfDay(hour) {
    const h = ((Math.floor(hour) % 24) + 24) % 24; // tolerate a bad clock rather than index off the end
    if (h >= 22 || h < 5)
        return 'night';
    if (h < 12)
        return 'morning';
    if (h < 17)
        return 'afternoon';
    return 'evening';
}
/**
 * Pick a greeting.
 *
 * `seed` decides which of the eligible lines is used; any integer works and the caller is expected
 * to hold one steady for as long as the greeting is visible. `hour` is the LOCAL hour.
 *
 * With no name, the `…Anon` variant of the same key is used rather than a separate shorter list —
 * so someone whose profile has no first name still gets the full rotation, and a translator sees
 * the two forms of one line side by side instead of two unrelated lists.
 */
export function pickGreeting(opts) {
    const part = partOfDay(opts.hour);
    const eligible = GREETINGS.filter((g) => g.when === part || g.when === 'any');
    // Non-negative modulo: a negative seed would index off the front and yield undefined.
    const i = ((Math.trunc(opts.seed) % eligible.length) + eligible.length) % eligible.length;
    const line = eligible[i];
    const name = opts.name?.trim();
    return name ? { key: `ai.greet.${line.key}`, name } : { key: `ai.greet.${line.key}Anon`, name: null };
}
/** A seed that changes between visits but not during one. Called once when a composer mounts. */
export function greetingSeed() {
    return Math.floor(Math.random() * 1e9);
}
//# sourceMappingURL=greeting.js.map